import { UserModel } from '../models/user.model'

/**
 * Classe utilitaire pour gérer les techniciens
 */
export class TechnicianTools {
  /**
   * Récupère les initiales d'un technicien à partir de son prénom et nom
   * @param technician L'objet technicien
   * @returns Les initiales (2 caractères)
   */
  public static getInitials(technician: any): string {
    if (technician?.initials) return technician.initials
    if (!technician?.firstname || !technician?.lastname) return '??'

    return (
      technician.firstname.charAt(0).toUpperCase() +
      technician.lastname.charAt(0).toUpperCase()
    )
  }

  /**
   * Trouve un technicien par email dans une liste
   * @param email L'email du technicien
   * @param technicians La liste des techniciens
   * @returns Le technicien trouvé ou null
   */
  public static findByEmail(
    email: string,
    technicians: any[]
  ): UserModel | null {
    if (!email || !technicians) return null
    return technicians.find((tech) => tech.email === email) || null
  }

  /**
   * Trouve un technicien par ID dans une liste
   * @param id L'ID du technicien
   * @param technicians La liste des techniciens
   * @returns Le technicien trouvé ou null
   */
  public static findById(id: number, technicians: any[]): UserModel | null {
    if (!id || !technicians) return null
    return technicians.find((tech) => tech.id === id) || null
  }

  /**
   * Récupère la couleur d'un technicien
   * @param technician L'objet technicien
   * @param defaultColor La couleur par défaut
   * @returns La couleur du technicien
   */
  public static getColor(technician: any, defaultColor = '#666'): string {
    return technician?.color || defaultColor
  }

  /**
   * Récupère la ville du domicile d'un technicien
   * @param technician L'objet technicien
   * @returns La ville du domicile
   */
  public static getHomeCity(technician: any): string {
    if (!technician) return ''
    return (
      technician.homeCity ||
      technician.home_city ||
      technician.homeLocality ||
      technician.home_locality ||
      technician.locality ||
      ''
    )
  }

  /**
   * Vérifie si un technicien a des coordonnées de domicile valides
   * @param technician L'objet technicien
   * @returns true si les coordonnées sont valides
   */
  public static hasValidHomeCoordinates(technician: any): boolean {
    if (!technician) return false
    const lat = technician.homeLatitude || technician.home_latitude
    const lng = technician.homeLongitude || technician.home_longitude
    return !!(lat && lng && +lat !== 0 && +lng !== 0)
  }

  /**
   * Récupère le nom complet d'un technicien
   * @param technician L'objet technicien
   * @returns Le nom complet
   */
  public static getFullName(technician: any): string {
    if (!technician) return 'Technicien'
    if (technician.fullName) return technician.fullName
    if (technician.firstname && technician.lastname) {
      return `${technician.firstname} ${technician.lastname}`
    }
    return technician.name || 'Technicien'
  }

  /**
   * Prépare les données des techniciens en ajoutant le nom complet
   * @param users La liste des utilisateurs
   */
  public static prepareUserData(users: any[]): void {
    if (!users?.length) return
    users.forEach((user) => {
      user.fullName = this.getFullName(user)
    })
  }

  /**
   * Mappe les techniciens d'une intervention aux utilisateurs canoniques
   * @param interventionTechnicians Les techniciens de l'intervention
   * @param users La liste des utilisateurs
   * @returns Les utilisateurs correspondants
   */
  public static mapInterventionTechnicians(
    interventionTechnicians: any[],
    users: any[]
  ): UserModel[] {
    if (!interventionTechnicians?.length || !users?.length) return []

    return interventionTechnicians
      .map((tech) => {
        const found = users.find((u) => u.email === tech.email)
        return found || tech
      })
      .filter(
        (v, i, a) =>
          a.findIndex((x) =>
            x.id != null ? x.id === v.id : x.email === v.email
          ) === i
      )
  }
}
